import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { Box, Button, Chip, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import { map, sumBy } from 'lodash';
import React from 'react';

import { DuplicateTodoOptionGroup } from './annotationClassOptions';

interface DuplicateClassRecordsProps {
  duplicateGroups: DuplicateTodoOptionGroup[];
  isDeleting?: boolean;
  onDeleteDuplicates: (classNames: string[]) => void;
}

const DuplicateClassRecords: React.FC<DuplicateClassRecordsProps> = ({
  duplicateGroups,
  isDeleting = false,
  onDeleteDuplicates,
}) => {
  const duplicateRecordCount = sumBy(duplicateGroups, (group) => group.duplicateOptions.length);

  if (!duplicateRecordCount) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2">Duplicate records ({duplicateRecordCount})</Typography>
        <Chip size="small" color="warning" label="Cleanup required" />
        <Button
          size="small"
          color="warning"
          startIcon={<DeleteForeverOutlinedIcon />}
          disabled={isDeleting}
          onClick={() => onDeleteDuplicates(map(duplicateGroups, 'nameKey'))}
          sx={{ ml: 'auto' }}
        >
          Delete all duplicates
        </Button>
      </Box>
      <List disablePadding>
        {map(duplicateGroups, (group) => {
          const duplicateCount = group.duplicateOptions.length;
          const canonicalState = group.canonicalOption.deletedAt ? 'archived' : 'active';

          return (
            <ListItem
              key={group.nameKey}
              disableGutters
              secondaryAction={
                <Tooltip title={`Permanently delete duplicate ${group.label} records`}>
                  <span>
                    <IconButton
                      aria-label={`Permanently delete duplicate ${group.label} records`}
                      color="warning"
                      disabled={isDeleting}
                      onClick={() => onDeleteDuplicates([group.nameKey])}
                    >
                      <DeleteForeverOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <ListItemText
                primary={group.label}
                secondary={`${duplicateCount} duplicate ${
                  duplicateCount === 1 ? 'record' : 'records'
                } · keeping first ${canonicalState} class`}
              />
            </ListItem>
          );
        })}
      </List>
      <Typography variant="caption" color="text.secondary">
        Cleanup removes duplicate assignment records only. Annotations are unchanged.
      </Typography>
    </Box>
  );
};

export default DuplicateClassRecords;
